import { createReducer, on } from "@ngrx/store";

import { CompanyState } from "./company.models";

import * as paginationConstants from '../../../shared/constants/pagination.constants';
import * as companyActions from './company.actions';
import { SavingStatus } from "../../../shared/models/list.page.model";

export const companyInitialState: CompanyState = {
    companies: [],
    companiesPage: null,
    companiesPagination: {
        pageIndex: 0,
        pageSize: paginationConstants.DefaultPageSize,
    },
    companiesCount: 0,
    error: null,
    loading: false,
    companySaveInProgress: SavingStatus.NotStarted,
    companyOverview: null,
    users: [],
    usersPage: null,
    usersPagination: {
        pageIndex: 0,
        pageSize: paginationConstants.DefaultPageSize,
    },
    usersCount: 0,
    companyUserSaveInProgress: SavingStatus.NotStarted,
    roles: [],
    companyUserOverview: null
}

export const companyReducer = createReducer(
    companyInitialState,
    on(companyActions.loadCompanyList, (state) => ({
      ...state,
      loading: true  
    })),
    on(companyActions.loadCompanyListFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        error: errorMessage ?? 'Error'
    })),
    on(companyActions.loadCompanyListSuccess, (state, { companiesResponse, pageIndex, pageSize }) => ({
        ...state,
        loading: false,
        error: null,
        companies: companiesResponse,
        companiesPage: {
            ...state.companiesPage,
            pageIndex: pageIndex,
            pageSize: pageSize,
            totalCount: companiesResponse.length
        }
    })),
    on(companyActions.loadCompanyCount, (state) => ({
        ...state,
        loading: true  
    })),
    on(companyActions.loadCompanyCountFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        error: errorMessage ?? 'Error'
    })),
    on(companyActions.loadCompanyCountSuccess, (state, { countResponse }) => ({
        ...state,
        loading: false,
        error: null,
        companiesCount: countResponse
    })),
    on(companyActions.changeCompanyPagination, (state, { pageIndex, pageSize }) => ({
        ...state,
        companiesPagination: {
            pageIndex,
            pageSize
        }
    })),
    on(companyActions.updateCompany, (state) => ({
        ...state,
        loading: true,
        companySaveInProgress: SavingStatus.InProgress
    })),
    on(companyActions.updateCompanySuccess, (state) => ({
        ...state,
        loading: false,
        companySaveInProgress: SavingStatus.Successful
    })),
    on(companyActions.updateCompanyFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        companySaveInProgress: SavingStatus.Failure,
        error: errorMessage ?? 'ERROR'
    })),
    on(companyActions.insertNewCompany, (state) => ({
        ...state,
        loading: true,
        companySaveInProgress: SavingStatus.InProgress
    })),
    on(companyActions.insertNewCompanySuccess, (state) => ({
        ...state,
        loading: false,
        companySaveInProgress: SavingStatus.Successful
    })),
    on(companyActions.insertNewCompanyFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        companySaveInProgress: SavingStatus.Failure,
        error: errorMessage ?? 'ERROR'
    })),
    on(companyActions.resetCompanySavingStatus, (state) => ({
        ...state,
        companySaveInProgress: SavingStatus.NotStarted
    })),
    on(companyActions.loadCompanyOverview, (state) => ({
        ...state,
        loading: true
    })),
    on(companyActions.loadCompanyOverviewSuccess, (state, { result }) => ({
        ...state,
        loading: false,
        companyOverview: {
            ...result
        }
    })),
    on(companyActions.loadCompanyOverviewFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        error: errorMessage ?? 'ERROR'
    })),
    on(companyActions.loadUserList, (state) => ({
        ...state,
        loading: true  
    })),
    on(companyActions.loadUserListFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        error: errorMessage ?? 'Error'
    })),
    on(companyActions.loadUserListSuccess, (state, { usersResponse, pageIndex, pageSize }) => ({
        ...state,
        loading: false,
        error: null,
        users: usersResponse,
        usersPage: {
            ...state.usersPage,
            pageIndex: pageIndex,
            pageSize: pageSize,
            totalCount: usersResponse.length
        }
    })),
    on(companyActions.loadUserCount, (state) => ({
        ...state,
        loading: true  
    })),
    on(companyActions.loadUserCountFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        error: errorMessage ?? 'Error'
    })),
    on(companyActions.loadUserCountSuccess, (state, { countResponse }) => ({
        ...state,
        loading: false,
        error: null,
        usersCount: countResponse
    })),
    on(companyActions.changeUserPagination, (state, { pageIndex, pageSize }) => ({
        ...state,
        usersPagination: {
            pageIndex,
            pageSize
        }
    })),
    on(companyActions.loadAuthRoles, (state) => ({
        ...state,
        loading: true  
    })),
    on(companyActions.loadAuthRolesFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        error: errorMessage ?? 'Error'
    })),
    on(companyActions.loadAuthRolesSuccess, (state, { rolesResponse }) => ({
        ...state,
        loading: false,
        error: null,
        roles: rolesResponse
    })),
    on(companyActions.insertNewCompanyUser, (state) => ({
        ...state,
        loading: true,
        companyUserSaveInProgress: SavingStatus.InProgress
    })),
    on(companyActions.insertNewCompanyUserSuccess, (state) => ({
        ...state,
        loading: false,
        companyUserSaveInProgress: SavingStatus.Successful
    })),
    on(companyActions.insertNewCompanyUserFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        companyUserSaveInProgress: SavingStatus.Failure,
        error: errorMessage ?? 'ERROR'
    })),
    on(companyActions.resetCompanyUserSavingStatus, (state) => ({
        ...state,
        companyUserSaveInProgress: SavingStatus.NotStarted
    })),
    on(companyActions.loadCompanyUserOverview, (state) => ({
        ...state,
        loading: true
    })),
    on(companyActions.loadCompanyUserOverviewSuccess, (state, { result }) => ({
        ...state,
        loading: false,
        companyUserOverview: {
            ...result
        }
    })),
    on(companyActions.loadCompanyUserOverviewFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        error: errorMessage ?? 'ERROR'
    })),
    on(companyActions.updateUser, (state) => ({
        ...state,
        loading: true,
        companyUserSaveInProgress: SavingStatus.InProgress
    })),
    on(companyActions.updateUserSuccess, (state) => ({
        ...state,
        loading: false,
        companyUserSaveInProgress: SavingStatus.Successful
    })),
    on(companyActions.updateUserFailure, (state, { errorMessage }) => ({
        ...state,
        loading: false,
        companyUserSaveInProgress: SavingStatus.Failure,
        error: errorMessage ?? 'ERROR'
    }))
)
